import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import axios from 'axios';
import InputMask from 'react-input-mask';
import CurrencyInput from 'react-currency-input-field';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faInfoCircle, faBuilding, faUser, faClock, faChartLine,
    faImage, faTags, faCheck, faChevronDown, faChevronUp,
    faSpinner, faArrowRight, faMapMarkerAlt, faPhone,
    faEnvelope, faLock, faIdCard, faCalendarAlt,
} from '@fortawesome/free-solid-svg-icons';

// Adicionar junto aos outros imports, no topo do RegisterPage.js
import logoImage from '../../img/logo.png';


// ─── Design Tokens ────────────────────────────────────────────────────────────
const C = {
    primary:     '#2563eb',
    primaryLight:'#3b82f6',
    primaryDark: '#1d4ed8',
    success:     '#10b981',
    successDark: '#059669',
    warning:     '#f59e0b',
    danger:      '#ef4444',
    gray50:      '#f9fafb',
    gray100:     '#f3f4f6',
    gray200:     '#e5e7eb',
    gray300:     '#d1d5db',
    gray400:     '#9ca3af',
    gray500:     '#6b7280',
    gray600:     '#6b7280',
    gray700:     '#374151',
    gray900:     '#111827',
    white:       '#ffffff',
};

// ─── Estado inicial ───────────────────────────────────────────────────────────
const initialHorariosState = {
    dom: { ativo: false, manha_inicio: '08:00', manha_fim: '12:00', tarde_inicio: '13:00', tarde_fim: '18:00', noite_inicio: '18:30', noite_fim: '20:00' },
    seg: { ativo: true,  manha_inicio: '08:00', manha_fim: '12:00', tarde_inicio: '13:00', tarde_fim: '18:00', noite_inicio: '18:30', noite_fim: '20:00' },
    ter: { ativo: true,  manha_inicio: '08:00', manha_fim: '12:00', tarde_inicio: '13:00', tarde_fim: '18:00', noite_inicio: '18:30', noite_fim: '20:00' },
    qua: { ativo: true,  manha_inicio: '08:00', manha_fim: '12:00', tarde_inicio: '13:00', tarde_fim: '18:00', noite_inicio: '18:30', noite_fim: '20:00' },
    qui: { ativo: true,  manha_inicio: '08:00', manha_fim: '12:00', tarde_inicio: '13:00', tarde_fim: '18:00', noite_inicio: '18:30', noite_fim: '20:00' },
    sex: { ativo: true,  manha_inicio: '08:00', manha_fim: '12:00', tarde_inicio: '13:00', tarde_fim: '18:00', noite_inicio: '18:30', noite_fim: '20:00' },
    sab: { ativo: true,  manha_inicio: '08:00', manha_fim: '12:00', tarde_inicio: '13:00', tarde_fim: '18:00', noite_inicio: '18:30', noite_fim: '20:00' },
};

const diasDaSemana = {
    seg: 'Segunda', ter: 'Terça', qua: 'Quarta', qui: 'Quinta',
    sex: 'Sexta',   sab: 'Sábado', dom: 'Domingo',
};

// ─── Componentes de UI ────────────────────────────────────────────────────────

const FieldGroup = ({ label, required, hint, children, icon }) => (
    <div style={{ marginBottom: '16px' }}>
        {label && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '600', color: C.gray700, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {icon && <FontAwesomeIcon icon={icon} style={{ color: C.gray400, fontSize: '11px' }} />}
                {label}
                {required && <span style={{ color: C.danger }}>*</span>}
            </label>
        )}
        {children}
        {hint && <p style={{ margin: '4px 0 0', fontSize: '11px', color: C.gray400 }}>{hint}</p>}
    </div>
);

const Input = ({ style = {}, ...props }) => (
    <input
        style={{
            width: '100%',
            padding: '10px 14px',
            border: `1.5px solid ${C.gray200}`,
            borderRadius: '9px',
            fontSize: '14px',
            color: C.gray900,
            backgroundColor: C.white,
            outline: 'none',
            transition: 'border-color 0.15s, box-shadow 0.15s',
            fontFamily: 'inherit',
            boxSizing: 'border-box',
            ...style,
        }}
        onFocus={e => { e.target.style.borderColor = C.primaryLight; e.target.style.boxShadow = `0 0 0 3px ${C.primaryLight}18`; }}
        onBlur={e => { e.target.style.borderColor = C.gray200; e.target.style.boxShadow = 'none'; }}
        {...props}
    />
);

const SectionTitle = ({ icon, title, subtitle }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', paddingBottom: '12px', borderBottom: `1px solid ${C.gray100}` }}>
        <div style={{ width: '36px', height: '36px', borderRadius: '9px', background: `linear-gradient(135deg, ${C.primaryLight}20, ${C.primaryLight}10)`, border: `1px solid ${C.primaryLight}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.primaryLight, fontSize: '15px', flexShrink: 0 }}>
            <FontAwesomeIcon icon={icon} />
        </div>
        <div>
            <p style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: C.gray900 }}>{title}</p>
            {subtitle && <p style={{ margin: 0, fontSize: '12px', color: C.gray400 }}>{subtitle}</p>}
        </div>
    </div>
);

const AccordionSection = ({ isOpen, onToggle, step, title, subtitle, icon, color, children }) => (
    <div style={{ marginBottom: '12px', borderRadius: '14px', border: `1.5px solid ${isOpen ? color + '40' : C.gray200}`, overflow: 'hidden', transition: 'border-color 0.2s', boxShadow: isOpen ? `0 4px 16px ${color}12` : 'none' }}>
        <button
            type="button"
            onClick={onToggle}
            style={{
                width: '100%', padding: '18px 20px',
                display: 'flex', alignItems: 'center', gap: '14px',
                backgroundColor: isOpen ? `${color}06` : C.white,
                border: 'none', cursor: 'pointer', textAlign: 'left',
                fontFamily: 'inherit', transition: 'background-color 0.2s',
            }}
        >
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: `linear-gradient(135deg, ${color}, ${color}cc)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.white, fontSize: '16px', flexShrink: 0, boxShadow: `0 4px 10px ${color}40` }}>
                <FontAwesomeIcon icon={icon} />
            </div>
            <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '11px', fontWeight: '700', color, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Passo {step}</span>
                </div>
                <p style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: C.gray900 }}>{title}</p>
                <p style={{ margin: 0, fontSize: '12px', color: C.gray400 }}>{subtitle}</p>
            </div>
            <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} style={{ color: C.gray400, fontSize: '13px', flexShrink: 0 }} />
        </button>
        {isOpen && (
            <div style={{ padding: '24px', borderTop: `1px solid ${C.gray100}`, backgroundColor: C.white }}>
                {children}
            </div>
        )}
    </div>
);

// ─── Componente Principal ─────────────────────────────────────────────────────
const RegisterPage = () => {
    const navigate = useNavigate();
    const [openStep, setOpenStep] = useState(0); // 0 = passo 1, 1 = passo 2
    const [formData, setFormData] = useState({
        entidade: { nome: '', cnpj: '', telefone: '', cep: '', numero: '', endereco: '', bairro: '', cidade: '', estado: '', complemento: '' },
        user:     { nome: '', email: '', cpf: '', password: '', password_confirmation: '' },
        saldo:    { valor_inicial: '', data_corte: new Date().toISOString().split('T')[0] },
        agenda:   { duracao_padrao: 30, horarios: initialHorariosState },
        layout:   { logo: null },
        tags:     { tags_interesse: '' },
    });
    const [isLoading,       setIsLoading]       = useState(false);
    const [isCepLoading,    setIsCepLoading]    = useState(false);
    const [submitMessage,   setSubmitMessage]   = useState({ type: '', text: '' });
    const [termsAccepted,   setTermsAccepted]   = useState(false);
    const [showTermsModal,  setShowTermsModal]  = useState(false);
    const [privacyAccepted, setPrivacyAccepted] = useState(false);
    const [showPrivacyModal,setShowPrivacyModal]= useState(false);
    const [logoPreview,     setLogoPreview]     = useState(null);

    // ── Handlers (lógica original preservada) ────────────────────────────────
    const handleChange = (e) => {
        const { name, value } = e.target;
        const [section, field] = name.split('.');
        if (section === 'agenda' && field?.startsWith('ativo-')) {
            handleHorarioChange(field.split('-')[1], 'ativo', e.target.checked);
        } else {
            setFormData(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
        }
    };

    const handleHorarioChange = (dia, campo, valor) => {
        setFormData(prev => ({
            ...prev,
            agenda: { ...prev.agenda, horarios: { ...prev.agenda.horarios, [dia]: { ...prev.agenda.horarios[dia], [campo]: valor } } }
        }));
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        setFormData(prev => ({ ...prev, layout: { ...prev.layout, logo: file } }));
        setLogoPreview(file ? URL.createObjectURL(file) : null);
    };

    const handleCurrencyChange = (value, name) => {
        const [section, field] = name.split('.');
        setFormData(prev => ({ ...prev, [section]: { ...prev[section], [field]: value || '' } }));
    };

    const handleCepBlur = async (e) => {
        const cep = e.target.value.replace(/\D/g, '');
        if (cep.length !== 8) return;
        setIsCepLoading(true);
        setSubmitMessage({ type: '', text: '' });
        try {
            const { data } = await axios.get(`https://viacep.com.br/ws/${cep}/json/`);
            if (data.erro) {
                setSubmitMessage({ type: 'warning', text: 'CEP não encontrado. Preencha o endereço manualmente.' });
                setFormData(prev => ({ ...prev, entidade: { ...prev.entidade, endereco: '', bairro: '', cidade: '', estado: '' } }));
            } else {
                setFormData(prev => ({ ...prev, entidade: { ...prev.entidade, endereco: data.logradouro, bairro: data.bairro, cidade: data.localidade, estado: data.uf } }));
            }
        } catch {
            setSubmitMessage({ type: 'danger', text: 'Erro ao buscar o CEP. Tente novamente.' });
        } finally {
            setIsCepLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setSubmitMessage({ type: '', text: '' });

        const dataPayload = new FormData();
        dataPayload.append('entidade[nome]',        formData.entidade.nome);
        dataPayload.append('entidade[cnpj]',        formData.entidade.cnpj.replace(/\D/g, ''));
        dataPayload.append('entidade[telefone]',    formData.entidade.telefone.replace(/\D/g, ''));
        dataPayload.append('entidade[cep]',         formData.entidade.cep.replace(/\D/g, ''));
        dataPayload.append('entidade[endereco]',    formData.entidade.endereco);
        dataPayload.append('entidade[numero]',      formData.entidade.numero);
        dataPayload.append('entidade[complemento]', formData.entidade.complemento || '');
        dataPayload.append('entidade[bairro]',      formData.entidade.bairro);
        dataPayload.append('entidade[cidade]',      formData.entidade.cidade);
        dataPayload.append('entidade[estado]',      formData.entidade.estado);
        dataPayload.append('user[nome]',                 formData.user.nome);
        dataPayload.append('user[email]',                formData.user.email);
        dataPayload.append('user[cpf]',                  formData.user.cpf.replace(/\D/g, ''));
        dataPayload.append('user[password]',             formData.user.password);
        dataPayload.append('user[password_confirmation]',formData.user.password_confirmation);
        const valorInicial = parseFloat(String(formData.saldo.valor_inicial || '0').replace('.', '').replace(',', '.')) || 0;
        dataPayload.append('saldo[valor_inicial]',  valorInicial);
        dataPayload.append('saldo[data_corte]',     formData.saldo.data_corte);
        dataPayload.append('agenda',                JSON.stringify(formData.agenda));
        if (formData.layout.logo)        dataPayload.append('logo',            formData.layout.logo);
        if (formData.tags.tags_interesse) dataPayload.append('tags_interesse', formData.tags.tags_interesse);

        try {
            await axios.post('/api/onboarding/register-entity-user', dataPayload, { headers: { 'Content-Type': 'multipart/form-data' } });
            setSubmitMessage({ type: 'success', text: 'Cadastro realizado com sucesso! Redirecionando para o login...' });
            setTimeout(() => navigate('/login'), 3000);
        } catch (error) {
            const errors = error.response?.data;
            let msg = 'Ocorreu um erro ao realizar o cadastro.';
            if (errors && typeof errors === 'object') msg = Object.values(errors).flat().join(' ');
            else if (error.response?.data?.message) msg = error.response.data.message;
            setSubmitMessage({ type: 'danger', text: msg });
        } finally {
            setIsLoading(false);
        }
    };

    const canSubmit = termsAccepted && privacyAccepted && !isLoading;

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div style={{ minHeight: '100vh', backgroundColor: C.gray50, fontFamily: "'Inter', 'Segoe UI', sans-serif" }}>

            {/* ── Layout split ── */}
            <div style={{ display: 'flex', minHeight: '100vh' }}>

                {/* ── Painel esquerdo — decorativo (oculto em mobile) ── */}
                <div
                    className="register-left-panel"
                    style={{
                        width: '380px', minWidth: '380px',
                        background: 'linear-gradient(145deg, #0f172a 0%, #1e3a5f 40%, #1e40af 80%, #2563eb 100%)',
                        padding: '48px 40px',
                        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                        position: 'sticky', top: 0, height: '100vh',
                        overflow: 'hidden',
                    }}
                >
                    {/* Decorações */}
                    <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '220px', height: '220px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.05)' }} />
                    <div style={{ position: 'absolute', bottom: '-80px', left: '-40px', width: '200px', height: '200px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.04)' }} />

                    {/* Logo */}
                    <div>
                        <img src={logoImage} alt="FluxoClínica" style={{ maxHeight: '48px', objectFit: 'contain', marginBottom: '40px' }} />

                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 14px', borderRadius: '999px', backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', marginBottom: '20px' }}>
                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: C.success, boxShadow: `0 0 8px ${C.success}` }} />
                            <span style={{ fontSize: '12px', fontWeight: '600', color: 'rgba(255,255,255,0.9)' }}>Gratuito para começar</span>
                        </div>

                        <h2 style={{ fontSize: '28px', fontWeight: '800', color: C.white, lineHeight: '1.3', margin: '0 0 14px', letterSpacing: '-0.02em' }}>
                            Configure sua clínica em minutos
                        </h2>
                        <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.65)', lineHeight: '1.7', margin: '0 0 36px' }}>
                            Preencha o formulário e tenha acesso imediato a todos os módulos do sistema.
                        </p>

                        {/* Features */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                            {[
                                { icon: '📅', title: 'Agenda Inteligente',  desc: 'Consultas e prontuários integrados' },
                                { icon: '💰', title: 'Financeiro Completo', desc: 'Faturamento, DRE e fluxo de caixa'   },
                                { icon: '📦', title: 'Estoque Organizado',  desc: 'Controle automático de materiais'    },
                                { icon: '👥', title: 'Gestão de Equipe',    desc: 'Perfis de acesso personalizados'     },
                            ].map(f => (
                                <div key={f.title} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div style={{ width: '40px', height: '40px', borderRadius: '10px', backgroundColor: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0 }}>
                                        {f.icon}
                                    </div>
                                    <div>
                                        <p style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: C.white }}>{f.title}</p>
                                        <p style={{ margin: 0, fontSize: '11px', color: 'rgba(255,255,255,0.55)' }}>{f.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Rodapé do painel */}
                    <div>
                        <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                            {[
                                { value: '200+',  label: 'Clínicas'    },
                                { value: '100%',  label: 'Gratuito'    },
                                { value: '6',     label: 'Módulos'     },
                            ].map(s => (
                                <div key={s.label} style={{ textAlign: 'center', flex: 1, padding: '10px', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.12)' }}>
                                    <p style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: C.white }}>{s.value}</p>
                                    <p style={{ margin: 0, fontSize: '10px', color: 'rgba(255,255,255,0.5)' }}>{s.label}</p>
                                </div>
                            ))}
                        </div>
                        <p style={{ margin: 0, fontSize: '11px', color: 'rgba(255,255,255,0.35)', textAlign: 'center' }}>
                            © {new Date().getFullYear()} FluxoClínica
                        </p>
                    </div>
                </div>

                {/* ── Painel direito — formulário ── */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '48px 40px' }} className="register-right-panel">
                    <div style={{ maxWidth: '680px', margin: '0 auto' }}>

                        {/* Header mobile */}
                        <div className="register-mobile-header" style={{ display: 'none', textAlign: 'center', marginBottom: '32px' }}>
                        <img src={logoImage} alt="FluxoClínica" style={{ maxHeight: '48px', marginBottom: '16px' }} />
                        </div>

                        {/* Título */}
                        <div style={{ marginBottom: '32px' }}>
                            <h1 style={{ fontSize: '28px', fontWeight: '800', color: C.gray900, margin: '0 0 8px', letterSpacing: '-0.02em' }}>
                                Crie sua conta 🎉
                            </h1>
                            <p style={{ margin: 0, fontSize: '15px', color: C.gray500, lineHeight: '1.6' }}>
                                Configure sua clínica em um único passo. Já tem conta?{' '}
                                <Link to="/login" style={{ color: C.primaryLight, fontWeight: '600', textDecoration: 'none' }}>Fazer login</Link>
                            </p>
                        </div>

                        {/* Progresso visual */}
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '28px' }}>
                            {[
                                { label: 'Dados obrigatórios', color: C.primaryLight },
                                { label: 'Configurações opcionais', color: C.success },
                            ].map((s, i) => (
                                <div key={i} style={{ flex: 1 }}>
                                    <div style={{ height: '4px', borderRadius: '999px', backgroundColor: openStep === i || (i === 0) ? s.color : C.gray200, marginBottom: '4px', transition: 'background-color 0.3s' }} />
                                    <p style={{ margin: 0, fontSize: '10px', color: C.gray400, fontWeight: '500' }}>{s.label}</p>
                                </div>
                            ))}
                        </div>

                        {/* Mensagem de status */}
                        {submitMessage.text && (
                            <div style={{
                                padding: '12px 16px', borderRadius: '10px', marginBottom: '20px',
                                display: 'flex', alignItems: 'flex-start', gap: '10px',
                                backgroundColor: submitMessage.type === 'success' ? '#f0fdf4' : submitMessage.type === 'danger' ? '#fef2f2' : '#fffbeb',
                                border: `1px solid ${submitMessage.type === 'success' ? '#86efac' : submitMessage.type === 'danger' ? '#fecaca' : '#fde68a'}`,
                            }}>
                                <span style={{ fontSize: '16px', flexShrink: 0 }}>
                                    {submitMessage.type === 'success' ? '✅' : submitMessage.type === 'danger' ? '❌' : '⚠️'}
                                </span>
                                <p style={{ margin: 0, fontSize: '13px', color: submitMessage.type === 'success' ? '#166534' : submitMessage.type === 'danger' ? '#991b1b' : '#92400e', lineHeight: '1.5' }}>
                                    {submitMessage.text}
                                </p>
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>

                            {/* ── PASSO 1 ── */}
                            <AccordionSection
                                isOpen={openStep === 0}
                                onToggle={() => setOpenStep(openStep === 0 ? -1 : 0)}
                                step={1} icon={faBuilding} color={C.primaryLight}
                                title="Dados da Clínica e Responsável"
                                subtitle="Obrigatório — configure o perfil principal"
                            >
                                {/* Dados da Clínica */}
                                <SectionTitle icon={faBuilding} title="Dados da Clínica" subtitle="Informações do seu estabelecimento" />

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                    <FieldGroup label="Nome da Clínica" required>
                                        <Input type="text" name="entidade.nome" value={formData.entidade.nome} onChange={handleChange} placeholder="Ex: Clínica Saúde Total" required />
                                    </FieldGroup>
                                    <FieldGroup label="CNPJ" required>
                                        <InputMask mask="99.999.999/9999-99" value={formData.entidade.cnpj} onChange={handleChange}>
                                            {(p) => <Input {...p} name="entidade.cnpj" placeholder="00.000.000/0000-00" required />}
                                        </InputMask>
                                    </FieldGroup>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                    <FieldGroup label="CEP" required>
                                        <div style={{ position: 'relative' }}>
                                            <InputMask mask="99999-999" value={formData.entidade.cep} onChange={handleChange} onBlur={handleCepBlur}>
                                                {(p) => <Input {...p} name="entidade.cep" placeholder="00000-000" required style={{ paddingRight: isCepLoading ? '36px' : '14px' }} />}
                                            </InputMask>
                                            {isCepLoading && (
                                                <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }}>
                                                    <FontAwesomeIcon icon={faSpinner} spin style={{ color: C.primaryLight, fontSize: '14px' }} />
                                                </div>
                                            )}
                                        </div>
                                    </FieldGroup>
                                    <FieldGroup label="Telefone" required>
                                        <InputMask mask="(99) 99999-9999" value={formData.entidade.telefone} onChange={handleChange}>
                                            {(p) => <Input {...p} name="entidade.telefone" placeholder="(00) 00000-0000" required />}
                                        </InputMask>
                                    </FieldGroup>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 1fr', gap: '12px' }}>
                                    <FieldGroup label="Endereço" required>
                                        <Input type="text" name="entidade.endereco" value={formData.entidade.endereco} onChange={handleChange} placeholder="Rua, Avenida..." required />
                                    </FieldGroup>
                                    <FieldGroup label="Nº" required>
                                        <Input type="text" name="entidade.numero" value={formData.entidade.numero} onChange={handleChange} placeholder="123" required />
                                    </FieldGroup>
                                    <FieldGroup label="Complemento">
                                        <Input type="text" name="entidade.complemento" value={formData.entidade.complemento} onChange={handleChange} placeholder="Sala, Andar..." />
                                    </FieldGroup>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px', gap: '12px' }}>
                                    <FieldGroup label="Bairro" required>
                                        <Input type="text" name="entidade.bairro" value={formData.entidade.bairro} onChange={handleChange} placeholder="Bairro" required />
                                    </FieldGroup>
                                    <FieldGroup label="Cidade" required>
                                        <Input type="text" name="entidade.cidade" value={formData.entidade.cidade} onChange={handleChange} placeholder="Cidade" required />
                                    </FieldGroup>
                                    <FieldGroup label="UF" required>
                                        <Input type="text" name="entidade.estado" value={formData.entidade.estado} onChange={handleChange} placeholder="SP" required maxLength="2" style={{ textTransform: 'uppercase' }} />
                                    </FieldGroup>
                                </div>

                                {/* Dados do Representante */}
                                <div style={{ marginTop: '28px' }}>
                                    <SectionTitle icon={faUser} title="Dados do Representante Legal" subtitle="Seu acesso de administrador ao sistema" />
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                    <FieldGroup label="Nome Completo" required>
                                        <Input type="text" name="user.nome" value={formData.user.nome} onChange={handleChange} placeholder="Seu nome completo" required />
                                    </FieldGroup>
                                    <FieldGroup label="CPF" required>
                                        <InputMask mask="999.999.999-99" value={formData.user.cpf} onChange={handleChange}>
                                            {(p) => <Input {...p} name="user.cpf" placeholder="000.000.000-00" required />}
                                        </InputMask>
                                    </FieldGroup>
                                </div>

                                <FieldGroup label="Email de Acesso" required>
                                    <Input type="email" name="user.email" value={formData.user.email} onChange={handleChange} placeholder="seu@email.com" required />
                                </FieldGroup>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                    <FieldGroup label="Senha" required>
                                        <Input type="password" name="user.password" value={formData.user.password} onChange={handleChange} placeholder="Mínimo 8 caracteres" required />
                                    </FieldGroup>
                                    <FieldGroup label="Confirmar Senha" required>
                                        <Input type="password" name="user.password_confirmation" value={formData.user.password_confirmation} onChange={handleChange} placeholder="Repita a senha" required />
                                    </FieldGroup>
                                </div>

                                {/* Horários */}
                                <div style={{ marginTop: '28px' }}>
                                    <SectionTitle icon={faClock} title="Horários de Atendimento" subtitle="Configure os dias e horários da sua clínica" />
                                </div>

                                <FieldGroup label="Duração padrão da consulta (minutos)">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        {[15, 20, 30, 45, 60].map(min => (
                                            <button
                                                key={min} type="button"
                                                onClick={() => setFormData(prev => ({ ...prev, agenda: { ...prev.agenda, duracao_padrao: min } }))}
                                                style={{
                                                    padding: '8px 16px', borderRadius: '8px', border: `1.5px solid ${formData.agenda.duracao_padrao === min ? C.primaryLight : C.gray200}`,
                                                    backgroundColor: formData.agenda.duracao_padrao === min ? `${C.primaryLight}15` : C.white,
                                                    color: formData.agenda.duracao_padrao === min ? C.primaryLight : C.gray500,
                                                    fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
                                                    transition: 'all 0.15s',
                                                }}
                                            >
                                                {min}min
                                            </button>
                                        ))}
                                    </div>
                                </FieldGroup>

                                {/* Tabela de horários */}
                                <div style={{ borderRadius: '10px', border: `1px solid ${C.gray200}`, overflow: 'hidden' }}>
                                    {/* Header */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr 1fr 1fr', gap: '0', padding: '10px 14px', backgroundColor: C.gray50, borderBottom: `1px solid ${C.gray200}` }}>
                                        <span style={{ fontSize: '11px', fontWeight: '700', color: C.gray500, textTransform: 'uppercase' }}>Dia</span>
                                        {['Manhã início', 'Manhã fim', 'Tarde início', 'Tarde fim'].map(h => (
                                            <span key={h} style={{ fontSize: '10px', fontWeight: '600', color: C.gray400, textAlign: 'center' }}>{h}</span>
                                        ))}
                                    </div>

                                    {Object.entries(diasDaSemana).map(([diaKey, diaNome], idx) => {
                                        const dia = formData.agenda.horarios[diaKey];
                                        return (
                                            <div key={diaKey} style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr 1fr 1fr', gap: '8px', padding: '10px 14px', borderBottom: idx < 6 ? `1px solid ${C.gray100}` : 'none', backgroundColor: dia.ativo ? C.white : C.gray50, alignItems: 'center', transition: 'background-color 0.15s' }}>
                                                {/* Toggle */}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <div
                                                        onClick={() => handleHorarioChange(diaKey, 'ativo', !dia.ativo)}
                                                        style={{ width: '36px', height: '20px', borderRadius: '999px', backgroundColor: dia.ativo ? C.primaryLight : C.gray300, cursor: 'pointer', position: 'relative', transition: 'background-color 0.2s', flexShrink: 0 }}
                                                    >
                                                        <div style={{ position: 'absolute', top: '2px', left: dia.ativo ? '18px' : '2px', width: '16px', height: '16px', borderRadius: '50%', backgroundColor: C.white, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                                                    </div>
                                                    <span style={{ fontSize: '13px', fontWeight: dia.ativo ? '600' : '400', color: dia.ativo ? C.gray900 : C.gray400 }}>{diaNome}</span>
                                                </div>

                                                {/* Inputs de horário */}
                                                {['manha_inicio', 'manha_fim', 'tarde_inicio', 'tarde_fim'].map(campo => (
                                                    <input
                                                        key={campo}
                                                        type="time"
                                                        value={dia[campo]}
                                                        onChange={e => handleHorarioChange(diaKey, campo, e.target.value)}
                                                        disabled={!dia.ativo}
                                                        style={{
                                                            padding: '6px 8px', border: `1px solid ${C.gray200}`, borderRadius: '7px',
                                                            fontSize: '12px', color: dia.ativo ? C.gray900 : C.gray400,
                                                            backgroundColor: dia.ativo ? C.white : C.gray100,
                                                            outline: 'none', fontFamily: 'inherit', width: '100%',
                                                            cursor: dia.ativo ? 'text' : 'not-allowed',
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Botão avançar */}
                                <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                                    <button
                                        type="button"
                                        onClick={() => setOpenStep(1)}
                                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', background: `linear-gradient(135deg, ${C.primaryLight}, ${C.primaryDark})`, color: C.white, border: 'none', borderRadius: '9px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' }}
                                    >
                                        Próximo: Configurações opcionais
                                        <FontAwesomeIcon icon={faArrowRight} />
                                    </button>
                                </div>
                            </AccordionSection>

                            {/* ── PASSO 2 ── */}
                            <AccordionSection
                                isOpen={openStep === 1}
                                onToggle={() => setOpenStep(openStep === 1 ? -1 : 1)}
                                step={2} icon={faChartLine} color={C.success}
                                title="Configurações Adicionais"
                                subtitle="Opcional — personalize ainda mais sua clínica"
                            >
                                {/* Financeiro */}
                                <SectionTitle icon={faChartLine} title="Configuração Financeira Inicial" subtitle="Defina o saldo de partida para controle preciso" />

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                    <FieldGroup label="Saldo Inicial em Caixa/Banco">
                                        <CurrencyInput
                                            id="saldo.valor_inicial" name="saldo.valor_inicial"
                                            placeholder="R$ 0,00"
                                            value={formData.saldo.valor_inicial}
                                            decimalsLimit={2} decimalSeparator="," groupSeparator="."
                                            onValueChange={(value, name) => handleCurrencyChange(value, name)}
                                            style={{ width: '100%', padding: '10px 14px', border: `1.5px solid ${C.gray200}`, borderRadius: '9px', fontSize: '14px', color: C.gray900, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                                        />
                                    </FieldGroup>
                                    <FieldGroup label="Data de Apuração" hint="Data em que o saldo foi apurado.">
                                        <Input type="date" name="saldo.data_corte" value={formData.saldo.data_corte} onChange={handleChange} />
                                    </FieldGroup>
                                </div>

                                {/* Logo */}
                                <div style={{ marginTop: '24px' }}>
                                    <SectionTitle icon={faImage} title="Logo da Clínica" subtitle="Aparecerá nos relatórios e no sistema" />
                                </div>

                                <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                                    {/* Preview */}
                                    <div style={{ width: '120px', height: '80px', borderRadius: '10px', border: `2px dashed ${C.gray300}`, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0, backgroundColor: C.gray50 }}>
                                        {logoPreview ? (
                                            <img src={logoPreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                        ) : (
                                            <div style={{ textAlign: 'center' }}>
                                                <FontAwesomeIcon icon={faImage} style={{ color: C.gray300, fontSize: '24px', display: 'block', marginBottom: '4px' }} />
                                                <span style={{ fontSize: '10px', color: C.gray400 }}>Sem logo</span>
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <FieldGroup label="Selecione um arquivo" hint="PNG, JPG ou GIF · Tamanho ideal: 300×150px">
                                            <input
                                                type="file" onChange={handleLogoChange} accept="image/png,image/jpeg,image/gif"
                                                style={{ width: '100%', padding: '8px', border: `1.5px solid ${C.gray200}`, borderRadius: '9px', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit', backgroundColor: C.white }}
                                            />
                                        </FieldGroup>
                                    </div>
                                </div>

                                {/* Tags */}
                                <div style={{ marginTop: '24px' }}>
                                    <SectionTitle icon={faTags} title="Tags de Interesse para Pacientes" subtitle="Segmente seus pacientes por procedimentos de interesse" />
                                </div>

                                <FieldGroup hint="Separe as tags por vírgula. Ex: Botox, Preenchimento, Harmonização Facial">
                                    <Input
                                        type="text" name="tags.tags_interesse"
                                        value={formData.tags.tags_interesse} onChange={handleChange}
                                        placeholder="Ex: Botox, Preenchimento Labial, Harmonização Facial"
                                    />
                                </FieldGroup>
                            </AccordionSection>

                            {/* ── Termos e CTA ── */}
                            <div style={{ marginTop: '24px', padding: '24px', backgroundColor: C.white, borderRadius: '14px', border: `1px solid ${C.gray200}` }}>

                                {/* Checkboxes */}
                                {[
                                    { id: 'terms',   checked: termsAccepted,   onChange: setTermsAccepted,   label: 'Termos de Serviço',      onClick: () => setShowTermsModal(true)   },
                                    { id: 'privacy', checked: privacyAccepted, onChange: setPrivacyAccepted, label: 'Política de Privacidade', onClick: () => setShowPrivacyModal(true) },
                                ].map(item => (
                                    <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                                        <div
                                            onClick={() => item.onChange(!item.checked)}
                                            style={{
                                                width: '20px', height: '20px', borderRadius: '5px', flexShrink: 0,
                                                border: `2px solid ${item.checked ? C.primaryLight : C.gray300}`,
                                                backgroundColor: item.checked ? C.primaryLight : C.white,
                                                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                transition: 'all 0.15s',
                                            }}
                                        >
                                            {item.checked && <FontAwesomeIcon icon={faCheck} style={{ color: C.white, fontSize: '10px' }} />}
                                        </div>
                                        <p style={{ margin: 0, fontSize: '13px', color: C.gray600 }}>
                                            Eu li e concordo com os{' '}
                                            <button type="button" onClick={item.onClick} style={{ background: 'none', border: 'none', color: C.primaryLight, fontWeight: '600', cursor: 'pointer', padding: 0, fontSize: '13px', fontFamily: 'inherit', textDecoration: 'underline' }}>
                                                {item.label}
                                            </button>
                                        </p>
                                    </div>
                                ))}

                                {/* Botão submit */}
                                <button
                                    type="submit"
                                    disabled={!canSubmit}
                                    style={{
                                        width: '100%', marginTop: '8px',
                                        padding: '14px',
                                        background: canSubmit
                                            ? `linear-gradient(135deg, ${C.success}, ${C.successDark})`
                                            : C.gray200,
                                        color: canSubmit ? C.white : C.gray400,
                                        border: 'none', borderRadius: '12px',
                                        fontSize: '16px', fontWeight: '800',
                                        cursor: canSubmit ? 'pointer' : 'not-allowed',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                                        fontFamily: 'inherit',
                                        boxShadow: canSubmit ? `0 6px 20px ${C.success}40` : 'none',
                                        transition: 'all 0.2s',
                                        letterSpacing: '-0.01em',
                                    }}
                                >
                                    {isLoading ? (
                                        <>
                                            <FontAwesomeIcon icon={faSpinner} spin />
                                            Criando sua conta...
                                        </>
                                    ) : (
                                        <>
                                            🚀 Criar Conta e Iniciar Gratuitamente
                                            <FontAwesomeIcon icon={faArrowRight} />
                                        </>
                                    )}
                                </button>

                                <p style={{ textAlign: 'center', marginTop: '12px', fontSize: '12px', color: C.gray400 }}>
                                    🔒 Seus dados estão seguros e criptografados
                                </p>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* ── Modais (lógica original preservada) ── */}
            {[
                {
                    show: showTermsModal, onHide: () => setShowTermsModal(false),
                    title: '📋 Termos de Serviço',
                    content: (
                        <>
                            <p><strong>Última atualização:</strong> 01/08/2025</p>
                            <p>Bem-vindo à nossa plataforma! Ao se registrar, você concorda com os seguintes termos.</p>
                            {[
                                ['1. Aceitação dos Termos', 'Ao acessar ou usar a plataforma, você confirma que leu, entendeu e concorda em ficar vinculado a estes Termos de Serviço.'],
                                ['2. Uso da Plataforma', 'Você concorda em usar a plataforma apenas para fins legais. Você é responsável por manter a confidencialidade de sua senha e conta.'],
                                ['3. Coleta e Uso de Dados', 'Coletamos informações pessoais e da sua clínica para fornecer e melhorar nossos serviços. Nossa Política de Privacidade descreve como protegemos seus dados.'],
                                ['4. Propriedade Intelectual', 'Todo o conteúdo da plataforma é de nossa propriedade ou de nossos licenciadores e é protegido por leis de direitos autorais.'],
                                ['5. Limitação de Responsabilidade', 'A plataforma é fornecida "como está". Não garantimos que o serviço será ininterrupto ou livre de erros.'],
                                ['6. Planos, Pagamentos e Limites', 'O acesso é concedido via assinatura mensal. Cada plano define um limite de créditos mensais. Créditos não utilizados expiram ao final do ciclo.'],
                            ].map(([h, p]) => (
                                <div key={h} style={{ marginBottom: '16px' }}>
                                    <h6 style={{ fontWeight: '700', color: C.gray900 }}>{h}</h6>
                                    <p style={{ color: C.gray600, fontSize: '14px', lineHeight: '1.6' }}>{p}</p>
                                </div>
                            ))}
                        </>
                    ),
                },
                {
                    show: showPrivacyModal, onHide: () => setShowPrivacyModal(false),
                    title: '🔒 Política de Privacidade',
                    content: (
                        <>
                            <p><strong>Última atualização:</strong> 01/08/2025</p>
                            {[
                                ['1. Informações que coletamos', 'Coletamos nome, e-mail, CPF, CNPJ e informações da clínica quando você se registra.'],
                                ['2. Como usamos suas informações', 'Usamos para operar, manter e fornecer os recursos da plataforma, comunicações e processamento de transações.'],
                                ['3. Compartilhamento', 'Não compartilhamos suas informações com terceiros, exceto para cumprir a lei ou proteger nossos direitos.'],
                            ].map(([h, p]) => (
                                <div key={h} style={{ marginBottom: '16px' }}>
                                    <h6 style={{ fontWeight: '700', color: C.gray900 }}>{h}</h6>
                                    <p style={{ color: C.gray600, fontSize: '14px', lineHeight: '1.6' }}>{p}</p>
                                </div>
                            ))}
                        </>
                    ),
                },
            ].map(modal => (
                <Modal key={modal.title} show={modal.show} onHide={modal.onHide} size="lg" centered>
                    <Modal.Header closeButton style={{ borderBottom: `1px solid ${C.gray100}` }}>
                        <Modal.Title style={{ fontSize: '16px', fontWeight: '700' }}>{modal.title}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto', padding: '24px' }}>
                        {modal.content}
                    </Modal.Body>
                    <Modal.Footer style={{ borderTop: `1px solid ${C.gray100}` }}>
                        <button onClick={modal.onHide} style={{ padding: '9px 20px', backgroundColor: C.gray100, color: C.gray700, border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', fontFamily: 'inherit' }}>
                            Fechar
                        </button>
                    </Modal.Footer>
                </Modal>
            ))}

            {/* ── CSS responsivo ── */}
            <style>{`
                * { box-sizing: border-box; }

                @media (max-width: 900px) {
                    .register-left-panel {
                        display: none !important;
                    }
                    .register-right-panel {
                        padding: 24px 20px !important;
                    }
                    .register-mobile-header {
                        display: block !important;
                    }
                }

                @media (max-width: 600px) {
                    .register-right-panel {
                        padding: 16px !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default RegisterPage;
